import { getRequiredSlots } from '../gphSkillRegistry';

const PLATFORM_ALIASES = {
  android: 'android',
  androide: 'android',
  iphone: 'ios',
  ios: 'ios',
  apple: 'ios'
};

const PLAY_STYLE_ALIASES = {
  rush: 'rush',
  rusher: 'rush',
  agressivo: 'rush',
  agressiva: 'rush',
  preciso: 'precise',
  precisa: 'precise',
  precise: 'precise',
  capa: 'precise',
  suporte: 'support',
  support: 'support'
};

const AIM_PROBLEM_ALIASES = {
  sobe_pouco: 'stays_on_chest',
  peito: 'stays_on_chest',
  'fica no peito': 'stays_on_chest',
  nao_sobe: 'stays_on_chest',
  'nao sobe': 'stays_on_chest',
  passa: 'passes_head',
  'passa da cabeca': 'passes_head',
  'passa da cabeça': 'passes_head',
  tremendo: 'shakes_too_much',
  treme: 'shakes_too_much',
  instavel: 'shakes_too_much',
  none: 'none',
  nenhum: 'none'
};

const SUPPORTED_PLATFORMS = new Set(['android', 'ios']);
const SUPPORTED_PLAY_STYLES = new Set(['rush', 'precise', 'support']);
const SUPPORTED_AIM_PROBLEMS = new Set(['stays_on_chest', 'passes_head', 'shakes_too_much', 'none']);

const stripAccents = (value = '') =>
  String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const cleanToken = (value = '') => stripAccents(value).replace(/[^a-z0-9:_]+/g, ' ').trim();

const normalizeEnum = (value, aliases) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const cleaned = cleanToken(raw);
  return aliases[cleaned] || raw;
};

const normalizeRam = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const raw = String(value).trim().toLowerCase();
  const numeric = Number(String(raw).replace(/[^\d.]/g, ''));
  if (Number.isFinite(numeric) && numeric > 0) {
    if (numeric >= 8) return '8gb';
    if (numeric >= 6) return '6gb';
    if (numeric >= 4) return '4gb';
    if (numeric >= 3) return '3gb';
    return '2gb';
  }
  return raw;
};

const toPositiveInteger = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

export const normalizeFreefireCalculatorSlots = (slots = {}) => {
  const normalized = { ...slots };
  normalized.platform = normalizeEnum(normalized.platform, PLATFORM_ALIASES);
  normalized.ram = normalizeRam(normalized.ram);
  normalized.fps = toPositiveInteger(normalized.fps);
  normalized.hz = toPositiveInteger(normalized.hz);
  normalized.fingers = toPositiveInteger(normalized.fingers);
  normalized.playStyle = normalizeEnum(normalized.playStyle, PLAY_STYLE_ALIASES);
  normalized.aimProblem = normalizeEnum(normalized.aimProblem, AIM_PROBLEM_ALIASES);
  normalized.deviceTier = normalized.deviceTier ? String(normalized.deviceTier).trim().toLowerCase().slice(0, 40) : null;
  normalized.deviceModel = normalized.deviceModel ? String(normalized.deviceModel).trim().slice(0, 80) : null;
  normalized.dpi = toPositiveInteger(normalized.dpi);
  normalized.buttonSize = toPositiveInteger(normalized.buttonSize);
  normalized.currentSens = (
    normalized.currentSens
    && typeof normalized.currentSens === 'object'
    && !Array.isArray(normalized.currentSens)
  ) ? normalized.currentSens : null;
  return normalized;
};

export const validateFreefireCalculatorSlots = (slots = {}) => {
  const normalizedSlots = normalizeFreefireCalculatorSlots(slots);
  const required = getRequiredSlots('freefire_calculator');
  const missingSlots = [];
  const invalidSlots = [];

  required.forEach((slot) => {
    if (!normalizedSlots[slot]) missingSlots.push(slot);
  });

  if (normalizedSlots.platform && !SUPPORTED_PLATFORMS.has(normalizedSlots.platform)) {
    invalidSlots.push({ slot: 'platform', value: normalizedSlots.platform, reason: 'unsupported_platform' });
  }

  if (normalizedSlots.playStyle && !SUPPORTED_PLAY_STYLES.has(normalizedSlots.playStyle)) {
    invalidSlots.push({ slot: 'playStyle', value: normalizedSlots.playStyle, reason: 'unsupported_play_style' });
  }

  if (normalizedSlots.aimProblem && !SUPPORTED_AIM_PROBLEMS.has(normalizedSlots.aimProblem)) {
    invalidSlots.push({ slot: 'aimProblem', value: normalizedSlots.aimProblem, reason: 'unsupported_aim_problem' });
  }

  ['fps', 'hz', 'fingers'].forEach((slot) => {
    if (slots[slot] !== undefined && normalizedSlots[slot] === null) {
      invalidSlots.push({ slot, value: slots[slot], reason: 'numeric_value_required' });
    }
  });

  return {
    valid: missingSlots.length === 0 && invalidSlots.length === 0,
    normalizedSlots,
    missingSlots,
    invalidSlots
  };
};

export const buildMissingFreefireSlotsMessage = (missingSlots = []) => {
  const missing = new Set(missingSlots);
  const deviceMissing = ['platform', 'ram'].some((slot) => missing.has(slot));
  const performanceMissing = ['fps', 'hz'].some((slot) => missing.has(slot));
  const gameplayMissing = ['fingers', 'playStyle', 'aimProblem'].some((slot) => missing.has(slot));

  if (deviceMissing) {
    return 'Me passa o basico do aparelho: Android ou iPhone e quantos GB de RAM. Se souber, diga tambem se ele e fraco, medio ou forte.';
  }
  if (performanceMissing) {
    return 'Agora me diga a performance: voce joga a quantos FPS e sua tela tem quantos Hz?';
  }
  if (gameplayMissing) {
    return 'Pra fechar, me diga quantos dedos usa, seu estilo de jogo e o erro da mira: fica no peito, passa da cabeca ou treme.';
  }
  return 'Preciso de mais alguns dados para calcular Free Fire com seguranca.';
};

export const buildInvalidFreefireSlotsMessage = (invalidSlots = []) => {
  const first = invalidSlots[0];
  if (!first) return null;

  if (first.reason === 'numeric_value_required') {
    return `Me manda ${first.slot} em numero, sem escrever por extenso.`;
  }
  if (first.reason === 'unsupported_platform') {
    return 'A plataforma precisa ser Android ou iPhone/iOS.';
  }
  if (first.reason === 'unsupported_play_style') {
    return 'O estilo precisa ser rush, preciso/capa ou suporte.';
  }
  if (first.reason === 'unsupported_aim_problem') {
    return 'O erro da mira precisa ser: fica no peito, passa da cabeca, treme ou nenhum.';
  }
  return 'Nao consegui validar esses dados para a Calculadora Free Fire.';
};

export default validateFreefireCalculatorSlots;
